import { GA4_EVENT } from './event';
import { generateSHA256Hash } from '../hashEmail';

const dataLayerPush = (ga4Params, eventCallback) => {
  if (eventCallback && typeof eventCallback === 'function') {
    ga4Params = {
      ...ga4Params,
      eventCallback: eventCallback,
    };
  }

  window['dataLayer'] && window['dataLayer'].push(ga4Params);
};

export const sendAddShipmentTemplateEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.ADD_SHIPMENT_TEMPLATE,
    template: ga4Params['template'],
  });
};

export const sendProductsChangeApprovalStatusEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.PRODUCTS_CHANGE_APPROVAL_STATUS,
    approval_status: ga4Params['approval_status'],
    quantity: ga4Params['quantity'],
  });
};

export const sendProductsDetailUpdateEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.PRODUCTS_DETAILS_UPDATE,
    update_area: ga4Params['update_area'],
    update_tab: ga4Params['update_tab'],
  });
};

export const sendProductsDeletedEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.PRODUCTS_DELETED,
    quantity: ga4Params['quantity'],
  });
};

export const sendProductListFilterEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.PRODUCT_LIST_FILTER,
    urun_adi: ga4Params['product_name'],
    stok_kodu: ga4Params['stock_code'],
    katalog_id: ga4Params['catalog_id'],
    gtin: ga4Params['gtin'],
    model_kodu: ga4Params['model_code'],
    urun_kodu: ga4Params['product_code'],
    kategori_sec: ga4Params['category'],
    marka_sec: ga4Params['brand'],
  });
};

export const sendViewProductDealsEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.VIEW_PRODUCT_DEALS,
    deal_status: ga4Params['deal_status'],
    user_id: ga4Params['user_id'],
  });
};

export const sendSearchProductEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.SEARCH_PRODUCT,
    search_term: ga4Params,
  });
};
export const sendProductFilteredSearchEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.PRODUCT_FILTERED_SEARCH,
    kategori_sec: ga4Params['kategori_sec'],
    marka_sec: ga4Params['marka_sec'],
  });
};
export const sendOpenProductForSaleEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.OPEN_PRODUCT_FOR_SALE,
    successCount: ga4Params,
  });
};
export const sendProductLinkClickEvent = () => {
  dataLayer.push({
    event: GA4_EVENT.PRODUCT_LINK_CLICK,
  });
};
export const sendProductDetailEvent = (eventName, ga4Params = {}) => {
  const {
    button_name,
    button_type,
    button_area,
    item_id,
    page_type,
    page_path,
    ...rest
  } = ga4Params;

  const resolvedItemId = Array.isArray(item_id)
    ? item_id.map((id) => String(id)).join('|')
    : item_id !== undefined
      ? String(item_id)
      : undefined;

  const payload = {
    event: eventName,
    page_path: page_path ?? getPagePathWithoutPrefix(),
    button_name: button_name,
    button_type: button_type,
    button_area: button_area,
    page_type: page_type ?? 'Product Details',
  };

  if (resolvedItemId !== undefined) {
    payload['item_id'] = resolvedItemId;
  }

  Object.keys(rest).forEach((key) => {
    if (rest[key] !== undefined) {
      payload[key] = rest[key];
    }
  });

  dataLayerPush(payload);
};

const getPagePathWithoutPrefix = () =>
  window.location.pathname.replace(/^\/selleroffice\/v2/, '') || '/';

export const sendMenuClickEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.MENU_CLICK,
    link_url: ga4Params?.link_url ?? '',
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
  });
};

export const sendUseAllBalanceEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.USE_ALL_BALANCE,
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
    page_type: 'Early Settlement',
  });
};

export const sendUserInfo = async (ga4Params) => {
  if (!ga4Params || !ga4Params.sellerId || !ga4Params.email) {
    return;
  }

  const hashedEmail = await generateSHA256Hash(ga4Params.email);
  dataLayer.push({
    user_id: ga4Params.sellerId,
    hashedEmail: hashedEmail,
  });
};
export const sendEarlySettlementCalculateEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.EARLY_SETTLEMENT_CALCULATE,
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
    page_type: 'Early Settlement',
  });
};

export const sendEarlySettlementCalculateDismissEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.EARLY_SETTLEMENT_CALCULATE_DISMISS,
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
    dismiss_reason: ga4Params?.dismiss_reason ?? '',
    page_type: 'Early Settlement',
  });
};

const toFloat = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value);
  const cleaned = str.includes(',')
    ? str.replace(/\./g, '').replace(',', '.')
    : str;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};

const getCommonEarlyPaymentParams = (ga4Params) => {
  return {
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
    total_amount_requested: toFloat(ga4Params?.total_amount_requested),
    days_paid_early: toFloat(ga4Params?.days_paid_early),
    n11_daily_commission_rate: toFloat(ga4Params?.n11_daily_commission_rate),
    service_rate: toFloat(ga4Params?.service_rate),
    service_fee: toFloat(ga4Params?.service_fee),
    tax: toFloat(ga4Params?.tax),
    value: toFloat(ga4Params?.value),
  };
};

export const sendRequestEarlyPaymentEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REQUEST_EARLY_PAYMENT,
    ...getCommonEarlyPaymentParams(ga4Params),
    page_type: 'Early Settlement',
  });
};

export const sendRequestEarlyPaymentSuccessEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REQUEST_EARLY_PAYMENT_SUCCESS,
    ...getCommonEarlyPaymentParams(ga4Params),
    early_payment_id: ga4Params?.early_payment_id ?? '',
    page_type: 'Early Settlement',
  });
};

export const sendRequestEarlyPaymentFailEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REQUEST_EARLY_PAYMENT_FAIL,
    ...getCommonEarlyPaymentParams(ga4Params),
    page_type: 'Early Settlement',
  });
};

export const sendRequestEarlyPaymentDismissEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REQUEST_EARLY_PAYMENT_DISMISS,
    ...getCommonEarlyPaymentParams(ga4Params),
    page_type: 'Early Settlement',
  });
};

export const sendButtonClickEvent = (ga4Params) => {
  const payload = {
    event: GA4_EVENT.BUTTON_CLICK,
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
    page_type: ga4Params?.page_type ?? '',
  };

  if (ga4Params?.button_type !== undefined && ga4Params?.button_type !== '') {
    payload['button_type'] = ga4Params.button_type;
  }

  if (ga4Params?.campaign_id !== undefined && ga4Params?.campaign_id !== '') {
    payload['campaign_id'] = ga4Params.campaign_id;
  }

  if (
    ga4Params?.campaign_name !== undefined &&
    ga4Params?.campaign_name !== ''
  ) {
    payload['campaign_name'] = ga4Params.campaign_name;
  }

  dataLayerPush(payload);
};

export const sendPageViewEvent = (ga4Params) => {
  const payload = {
    event: GA4_EVENT.PAGE_VIEW,
    page_path: getPagePathWithoutPrefix(),
    page_name: ga4Params?.page_name ?? '',
    page_type: ga4Params?.page_type ?? '',
  };

  if (ga4Params?.campaign_id !== undefined && ga4Params?.campaign_id !== '') {
    payload['campaign_id'] = ga4Params.campaign_id;
  }

  if (
    ga4Params?.campaign_name !== undefined &&
    ga4Params?.campaign_name !== ''
  ) {
    payload['campaign_name'] = ga4Params.campaign_name;
  }

  dataLayerPush(payload);
};

export const sendTemplateDownloadFailEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.TEMPLATE_DOWNLOAD_FAIL,
    page_path: getPagePathWithoutPrefix(),
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Template Detail',
    fail_reason: ga4Params?.fail_reason ?? '',
  });
};

export const sendRemoveProductsCampaignEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REMOVE_PRODUCTS_CAMPAIGN,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? 'Remove Products from Campaign',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
  });
};

export const sendRemoveProductsSuccessEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REMOVE_PRODUCTS_SUCCESS,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
  });
};

export const sendRemoveProductsFailEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REMOVE_PRODUCTS_FAIL,
    page_path: getPagePathWithoutPrefix(),
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
    value: ga4Params?.value ?? '',
    price: ga4Params?.price ?? '',
    commission_rate: ga4Params?.commission_rate ?? '',
  });
};

export const sendRemoveProductsDismissEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.REMOVE_PRODUCTS_DISMISS,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
    value: ga4Params?.value ?? '',
    price: ga4Params?.price ?? '',
    commission_rate: ga4Params?.commission_rate ?? '',
  });
};

export const sendAddProductsCampaignEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.ADD_PRODUCTS_CAMPAIGN,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
  });
};

export const sendAddProductsSuccessEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.ADD_PRODUCTS_SUCCESS,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
    value: ga4Params?.value ?? '',
    price: ga4Params?.price ?? '',
    commission_rate: ga4Params?.commission_rate ?? '',
  });
};

export const sendAddProductsFailEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.ADD_PRODUCTS_FAIL,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
  });
};

export const sendAddProductsDismissEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.ADD_PRODUCTS_DISMISS,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
  });
};

export const sendFilterShowResultEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.FILTER_SHOW_RESULT,
    page_path: getPagePathWithoutPrefix(),
    button_name: 'Filtrele',
    button_type: 'Filter',
    page_type: ga4Params?.page_type ?? 'Campaign Detail',
    filter_count: ga4Params?.filter_count ?? 0,
    selected_filters: ga4Params?.selected_filters ?? '',
    filters: ga4Params?.filters ?? [],
  });
};

export const sendJoinCampaignEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.JOIN_CAMPAIGN,
    page_path: getPagePathWithoutPrefix(),
    button_name: 'Filtrele',
    button_type: 'Filter',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: ga4Params?.page_type ?? 'Campaign Detail',
  });
};

export const sendSelectFileEvent = (ga4Params) => {
  const basePath = getPagePathWithoutPrefix();
  const fullPath = basePath.endsWith('/bulk') ? basePath : `${basePath}/bulk`;

  dataLayerPush({
    event: GA4_EVENT.SELECT_FILE,
    page_path: fullPath,
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    page_type: ga4Params?.page_type ?? '',
  });
};

export const sendSelectProductEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.SELECT_PRODUCT,
    link_url: ga4Params?.link_url ?? '',
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
  });
};

export const sendJoinCampaignSuccessEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.JOIN_CAMPAIGN_SUCCESS,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
    value: ga4Params?.value ?? '',
    price: ga4Params?.price ?? '',
    commission_rate: ga4Params?.commission_rate ?? '',
  });
};

export const sendJoinCampaignFailEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.JOIN_CAMPAIGN_FAIL,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
    value: ga4Params?.value ?? '',
    price: ga4Params?.price ?? '',
    commission_rate: ga4Params?.commission_rate ?? '',
  });
};

export const sendJoinCampaignDismissEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.JOIN_CAMPAIGN_DISMISS,
    page_path: getPagePathWithoutPrefix(),
    button_name: ga4Params?.button_name ?? '',
    button_type: ga4Params?.button_type ?? '',
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Campaign Detail',
    item_id: ga4Params?.item_id ?? '',
    value: ga4Params?.value ?? '',
    price: ga4Params?.price ?? '',
    commission_rate: ga4Params?.commission_rate ?? '',
  });
};

export const sendSortApplyEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.SORT_APPLY,
    page_path: getPagePathWithoutPrefix(),
    button_name: 'Sırala',
    button_type: 'Sort',
    sorting_options: ga4Params?.sorting_options ?? '',
    page_type: 'Cart Discount Campaigns',
  });
};

export const sendTemplateUploadSuccessEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.TEMPLATE_UPLOAD_SUCCESS,
    page_path: getPagePathWithoutPrefix(),
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Template Detail',
  });
};

export const sendTemplateUploadFailEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.TEMPLATE_UPLOAD_FAIL,
    page_path: getPagePathWithoutPrefix(),
    campaign_id: ga4Params?.campaign_id ?? '',
    campaign_name: ga4Params?.campaign_name ?? '',
    page_type: 'Template Detail',
    fail_reason: ga4Params?.fail_reason ?? '',
  });
};

export const sendGoToProductDetailClickEvent = () => {
  sendProductDetailEvent(GA4_EVENT.GO_TO_PRODUCT_DETAIL_CLICK, {
    page_path: '/product/products',
    button_name: 'Detaya Git',
    button_type: 'Go To Product Detail',
    button_area: 'Product Listing',
    page_type: 'Product Listing',
  });
};

export const sendEarlySettlementCheckboxEvent = (isChecked, ga4Params) => {
  const eventType = isChecked
    ? GA4_EVENT.CHECKBOX_CHECKED
    : GA4_EVENT.CHECKBOX_UNCHECKED;
  dataLayerPush({
    event: eventType,
    page_path: ga4Params?.page_path ?? '',
    button_name: ga4Params?.button_name ?? '',
    page_type: 'Early Settlement',
  });
};

export const sendBannerClickEvent = (ga4Params) => {
  dataLayerPush({
    event: GA4_EVENT.BANNER_CLICK,
    banner_id: ga4Params?.banner_id ?? '',
    banner_name: ga4Params?.banner_name ?? '',
    link_url: ga4Params?.link_url ?? '',
    page_path: ga4Params?.page_path ?? '',
    user_id: ga4Params?.user_id ?? '',
  });
};
